import React, { useMemo } from 'react';
import Image from 'next/legacy/image';
import { Asset } from 'contentful';

import { IAffiliateHeaderFields } from '../../../types/contentful';

import { getScaledImageHeight } from '../../utils/getScaledImageHeight';

import { GreenlightLogo } from '../GreenlightLogo';
import { Asset as AssetRenderer } from '../AtomicComponents/Asset';

import plusSign from '@/images/plus-sign-affiliates.svg';

const AFFILIATE_LOGO_MIN_HEIGHT_PX = 22;
const AFFILIATE_LOGO_MAX_HEIGHT_PX = 80;
const HEADER_HEIGHT_BASELINE_PX = 100;

export const getHeaderHeight = (asset?: Asset) => {
  const { file } = asset?.fields ?? {};

  const logoWidth = file?.details?.image?.width || 1;
  const logoHeight = file?.details?.image?.height || 1;

  const {
    imageHeight: affiliateLogoHeight,
    aspectRatio: affiliateLogoAspectRatio
  } = getScaledImageHeight(
    logoWidth,
    logoHeight,
    AFFILIATE_LOGO_MIN_HEIGHT_PX,
    AFFILIATE_LOGO_MAX_HEIGHT_PX
  );

  return {
    headerHeight:
      affiliateLogoAspectRatio > 3
        ? HEADER_HEIGHT_BASELINE_PX - 20
        : HEADER_HEIGHT_BASELINE_PX,
    affiliateLogoHeight
  };
};

export function AffiliateHeader({
  hideAffiliateLogo,
  affiliateLogo
}: IAffiliateHeaderFields) {
  const { description } = affiliateLogo?.fields ?? {};

  const { headerHeight, affiliateLogoHeight } = useMemo(
    () => getHeaderHeight(affiliateLogo),
    [affiliateLogo]
  );

  return (
    <div
      className="w-screen"
      style={{ height: `${headerHeight}px` }}
      data-testid="affiliate-header-1"
    >
      <div className="flex w-full flex-wrap justify-center">
        <div className="container">
          <div
            className="max-w-screen layout-padding mx-auto flex w-full items-center"
            style={{ height: `${headerHeight}px` }}
          >
            <div className="shrink grow basis-auto">
              <div className="mx-auto max-w-full desktop:mx-0">
                <GreenlightLogo isMenuOpen={false} />
              </div>
            </div>
            {!hideAffiliateLogo && (
              <>
                <Image
                  src={plusSign}
                  alt="Plus sign"
                  className="mx-4 desktop:mx-6"
                />
                <div className="shrink grow basis-auto">
                  {affiliateLogo && (
                    <AssetRenderer
                      asset={affiliateLogo}
                      alt={description ?? 'Affiliate of Greenlight logo'}
                      className="max-w-full"
                      style={{ maxHeight: affiliateLogoHeight }}
                      loading="eager"
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
